/**
 * Registrable domain ("public suffix plus one label") from a hostname.
 *
 * S-004, 2026-09-07: `checkDomainRateLimit` keyed its Redis bucket on the bare
 * hostname of the AOAuth issuer. One wildcard DNS record (`*.evil.com A ...`)
 * therefore bought an attacker an unbounded number of buckets — every
 * `a1.evil.com`, `a2.evil.com`, ... got its own fresh allowance of ten
 * auto-registered agents per hour, from a single registration. The domain
 * registration is the scarce thing an attacker has to pay for, so it is the
 * thing the bucket must be keyed on.
 *
 * "Last two labels" is not good enough on its own: it collides `foo.co.uk`
 * with `bar.co.uk` (two unrelated registrants sharing one bucket) and it
 * over-merges hosting suffixes where every subdomain is a separate tenant
 * (`a.github.io` is not `b.github.io`). So this is a small public-suffix list.
 *
 * It is a BUNDLED SNAPSHOT and deliberately not a network lookup: this runs on
 * the pre-auth registration path, so a DNS or HTTP dependency here would be a
 * denial-of-service lever and a second SSRF surface on the exact path S-001 was
 * about. The snapshot is the multi-label part of the Mozilla Public Suffix List
 * (https://publicsuffix.org/), taken 2026-09-07, trimmed to the ccTLD
 * second-levels and the tenant-per-subdomain hosting suffixes that actually
 * appear in agent URLs. Single-label suffixes (`com`, `io`, `ai`, ...) need no
 * entry: they are the default two-label rule.
 *
 * Consequence of a MISSING entry: the two labels rule applies, which merges
 * tenants of that suffix into one bucket. That is a false positive on a rate
 * limit, never a bypass. Adding an entry can only ever split buckets, so keep
 * the list conservative and let a legitimately squeezed hosting suffix earn its
 * line.
 *
 * Shared with the SMTP `ip:<registrable-domain>:<ip/24>` principal (M1-03), so
 * it lives in its own module with no db, redis or Next imports.
 */

/** Multi-label public suffixes. Snapshot of publicsuffix.org, 2026-09-07. */
const MULTI_LABEL_SUFFIXES: ReadonlySet<string> = new Set([
  // ── ccTLD second levels (ICANN section) ────────────────────────────────
  'ac.at', 'co.at', 'gv.at', 'or.at',
  'asn.au', 'com.au', 'edu.au', 'gov.au', 'id.au', 'net.au', 'org.au',
  'ac.be', 'com.br', 'net.br', 'org.br', 'gov.br', 'edu.br',
  'co.bw', 'ab.ca', 'bc.ca', 'on.ca', 'qc.ca',
  'com.cn', 'edu.cn', 'gov.cn', 'net.cn', 'org.cn', 'ac.cn',
  'com.co', 'net.co', 'nom.co',
  'ac.cr', 'co.cr', 'com.cy', 'com.de',
  'com.ec', 'com.eg', 'com.es', 'org.es', 'edu.es',
  'com.fj', 'co.gg', 'com.gh', 'com.gt',
  'com.hk', 'edu.hk', 'gov.hk', 'idv.hk', 'net.hk', 'org.hk',
  'co.hu', 'co.id', 'or.id', 'web.id',
  'co.il', 'org.il', 'net.il', 'ac.il', 'gov.il',
  'co.im', 'ac.in', 'co.in', 'edu.in', 'gov.in', 'net.in', 'org.in', 'firm.in', 'gen.in', 'ind.in',
  'co.ir', 'ac.ir', 'com.is',
  'ac.jp', 'ad.jp', 'co.jp', 'ed.jp', 'go.jp', 'lg.jp', 'ne.jp', 'or.jp',
  'co.ke', 'co.kr', 'ne.kr', 'or.kr', 're.kr', 'pe.kr', 'go.kr', 'ac.kr',
  'com.kw', 'com.lb', 'com.lk', 'co.ma', 'com.mt', 'com.mx', 'org.mx',
  'com.my', 'net.my', 'org.my', 'edu.my', 'gov.my',
  'com.ng', 'com.ni', 'co.nz', 'net.nz', 'org.nz', 'ac.nz', 'govt.nz', 'school.nz',
  'com.om', 'com.pa', 'com.pe', 'com.ph', 'com.pk', 'com.pl', 'net.pl', 'org.pl',
  'com.pt', 'com.py', 'com.qa', 'com.ro', 'com.ru', 'net.ru', 'org.ru',
  'com.sa', 'com.sg', 'edu.sg', 'net.sg', 'org.sg',
  'com.sv', 'co.th', 'ac.th', 'in.th', 'go.th',
  'com.tn', 'com.tr', 'net.tr', 'org.tr', 'edu.tr', 'gov.tr',
  'com.tw', 'edu.tw', 'gov.tw', 'net.tw', 'org.tw',
  'com.ua', 'net.ua', 'org.ua', 'co.ug', 'co.uz',
  'ac.uk', 'co.uk', 'gov.uk', 'judiciary.uk', 'ltd.uk', 'me.uk', 'net.uk',
  'nhs.uk', 'nic.uk', 'org.uk', 'plc.uk', 'police.uk', 'sch.uk',
  'com.uy', 'com.ve', 'com.vn', 'net.vn', 'org.vn', 'edu.vn', 'gov.vn',
  'co.za', 'org.za', 'net.za', 'web.za', 'co.zw',
  // ── gTLD second levels ────────────────────────────────────────────────
  'eu.com', 'gb.net', 'uk.com', 'uk.net', 'us.com', 'za.com',
  // ── Hosting suffixes: every subdomain is a different tenant ────────────
  // Without these, all of GitHub Pages (or Cloudflare Workers, or a tailnet)
  // would share one ten-per-hour bucket worldwide.
  'appspot.com', 'azurewebsites.net', 'azurestaticapps.net',
  'cloudfunctions.net', 'cloudfront.net', 'r2.dev', 's3.amazonaws.com',
  'cyclic.app', 'deno.dev', 'firebaseapp.com', 'web.app',
  'fly.dev', 'github.io', 'gitlab.io', 'glitch.me', 'herokuapp.com',
  'hf.space', 'koyeb.app', 'modal.run', 'ngrok.app', 'ngrok-free.app',
  'ngrok.io', 'netlify.app', 'onrender.com', 'pages.dev', 'workers.dev',
  'railway.app', 'up.railway.app', 'repl.co', 'replit.dev', 'replit.app',
  'surge.sh', 'trycloudflare.com', 'ts.net', 'val.run', 'vercel.app',
  'vercel.sh', 'zeabur.app',
]);

/**
 * Normalise a host the way the rate limiter needs it: lower case, no trailing
 * root dot, no port, IPv6 brackets stripped.
 */
function normaliseHost(host: string): string {
  let h = String(host || '').trim().toLowerCase();
  if (h.startsWith('[')) {
    const close = h.indexOf(']');
    if (close > 0) return h.slice(1, close);
  }
  // A port is never part of the registrable name. Only strip when there is
  // exactly ONE colon and a numeric tail (`host:8080`): an unbracketed IPv6
  // literal has several, and lopping off its last group would turn
  // `2001:db8::1` into `2001:db8::`, a different address.
  const colon = h.indexOf(':');
  if (colon > 0 && colon === h.lastIndexOf(':') && /^\d+$/.test(h.slice(colon + 1))) {
    h = h.slice(0, colon);
  }
  while (h.endsWith('.')) h = h.slice(0, -1);
  return h;
}

/** A bare IPv4/IPv6 literal is its own key: there is no domain to register. */
function isIpLiteral(host: string): boolean {
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(host)) return true;
  return host.includes(':');
}

/**
 * The registrable domain for `host`, or the normalised host itself when there
 * is nothing to fold (an IP literal, a single label, or a host that IS a public
 * suffix). Never throws: a caller on the rate-limit path must always get a key.
 */
export function registrableDomain(host: string): string {
  const h = normaliseHost(host);
  if (!h) return '';
  if (isIpLiteral(h)) return h;

  const labels = h.split('.').filter(Boolean);
  if (labels.length <= 2) return labels.join('.');

  // Longest matching suffix wins, so `s3.amazonaws.com` beats a hypothetical
  // `amazonaws.com` entry and the answer is the tenant, not the provider.
  for (let i = 0; i < labels.length - 1; i += 1) {
    const candidate = labels.slice(i).join('.');
    if (MULTI_LABEL_SUFFIXES.has(candidate)) {
      // One label above the suffix. `i === 0` means the whole host is the
      // suffix (`co.uk` on its own), which has no registrant; return it.
      return i === 0 ? candidate : labels.slice(i - 1).join('.');
    }
  }

  return labels.slice(-2).join('.');
}

/**
 * The registrable domain of a URL's host, or null when the URL does not parse.
 * Convenience for callers that hold a URL rather than a hostname.
 */
export function registrableDomainFromUrl(url: string): string | null {
  try {
    return registrableDomain(new URL(url).hostname) || null;
  } catch {
    return null;
  }
}
